import { useState, useEffect, useCallback, useRef, type FormEvent } from "react";
import {
  getConnectionStatus,
  connectSessionWithRole,
  getSessionQR,
  getSessionStatus,
  deleteSessionRecord,
  logoutSession,
} from "../services/whatsapp";
import type { WhatsAppSession, ConnectionStatusResponse } from "../types/whatsapp";
import { ROLES, STATUS_COLORS, STATUS_LABELS, getRolInfo } from "../types/whatsapp";
import QRCodeModal from "../components/QRCodeModal";

const POLLING_INTERVAL = 3000;

export default function SessionsPage() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [phone, setPhone] = useState("");
  const [label, setLabel] = useState("");
  const [rol, setRol] = useState("support");
  const [creating, setCreating] = useState(false);

  // QR display (inline, not modal)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [pollingStatus, setPollingStatus] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // QR modal for "Ver QR" button
  const [qrModal, setQrModal] = useState<{
    phone: string;
    rolLabel: string;
    qrDataUrl: string;
    status: string;
  } | null>(null);

  // Fetch connection status
  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const status = await getConnectionStatus();
      setConnectionStatus(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar estado");
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchStatus();
      setLoading(false);
    };
    init();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [fetchStatus]);

  // Start polling after QR is shown
  const startPolling = (sessionId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await getSessionStatus(sessionId);
        const newStatus = res.sesion?.status;
        setPollingStatus(newStatus || null);

        if (newStatus === "connected") {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setQrDataUrl(null);
          await fetchStatus(); // refresh full status
        }
      } catch {
        // ignore polling errors
      }
    }, POLLING_INTERVAL);
  };

  // Handle connect
  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const response = await connectSessionWithRole({ phone, label, rol });
      setPhone("");
      setLabel("");
      setRol("support");
      setShowCreate(false);

      const sessionId = response.sesion?.id_whatsapp_session;
      if (response.qr_data_url) {
        setQrDataUrl(response.qr_data_url);
        setPollingStatus(response.sesion?.status || "qr");
        if (sessionId) startPolling(sessionId);
      } else if (sessionId) {
        // QR was null, try fetching it
        try {
          const qrRes = await getSessionQR(sessionId);
          if (qrRes.qr_data_url) {
            setQrDataUrl(qrRes.qr_data_url);
            setPollingStatus("qr");
            startPolling(sessionId);
          }
        } catch {
          // QR not ready, try again in 3s
          setTimeout(async () => {
            try {
              const qrRes2 = await getSessionQR(sessionId);
              if (qrRes2.qr_data_url) {
                setQrDataUrl(qrRes2.qr_data_url);
                setPollingStatus("qr");
                startPolling(sessionId);
              }
            } catch { /* give up */ }
          }, 3000);
        }
      }
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al conectar");
    } finally {
      setCreating(false);
    }
  };

  // Show QR for existing session (modal)
  const handleShowQR = async (session: WhatsAppSession) => {
    const sessionId = session.id_whatsapp_session;
    if (!sessionId) return;
    try {
      const qrRes = await getSessionQR(sessionId);
      const rolInfo = getRolInfo(session.rol);
      setQrModal({
        phone: session.phone || "—",
        rolLabel: rolInfo.label,
        qrDataUrl: qrRes.qr_data_url,
        status: session.status,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al obtener QR");
    }
  };

  // Logout
  const handleLogout = async (session: WhatsAppSession) => {
    const sessionId = session.id_whatsapp_session;
    if (!sessionId) return;
    if (!confirm("¿Cerrar sesión de WhatsApp? Necesitarás escanear el QR nuevamente para conectar.")) return;
    try {
      await logoutSession(sessionId);
      setQrDataUrl(null);
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cerrar sesión");
    }
  };

  // Delete
  const handleDelete = async (session: WhatsAppSession) => {
    const sessionId = session.id_whatsapp_session;
    if (!sessionId) return;
    if (!confirm("¿Eliminar esta sesión permanentemente?")) return;
    try {
      await deleteSessionRecord(sessionId);
      setQrDataUrl(null);
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al eliminar");
    }
  };

  // Derive state
  const conectado = connectionStatus?.conectado || false;
  const sesiones = connectionStatus?.sesiones || [];
  const sesionActiva = connectionStatus?.sesion_activa;
  const noHaySesiones = sesiones.length === 0;
  const haySesionDesconectada = sesiones.length > 0 && !conectado;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Sesión WhatsApp</h2>
          <p className="page-subtitle">
            {conectado
              ? `✅ Conectado · ${sesionActiva?.phone || ""}`
              : noHaySesiones
                ? "No hay ninguna sesión. Conecta un número."
                : `Sesión: ${(sesiones[0]?.status && STATUS_LABELS[sesiones[0].status]) || sesiones[0]?.status || ""}`}
          </p>
        </div>
        {noHaySesiones && !showCreate && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Conectar Número
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-state"><div className="spinner" /><span>Cargando...</span></div>
      ) : (
        <>
          {/* CONNECTED STATE */}
          {conectado && sesionActiva && (
            <div className="card session-card">
              <div className="session-card-header">
                <div className="session-phone">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <span className="session-phone-number">{sesionActiva.phone || "—"}</span>
                </div>
                <div className="session-status-row">
                  <div className="session-status-dot" style={{ background: STATUS_COLORS.connected }} />
                  <span className="session-status-txt" style={{ color: STATUS_COLORS.connected }}>
                    {sesionActiva.rol_display || "Conectado"}
                  </span>
                </div>
              </div>
              <div className="session-card-body">
                <div className="rol-badge" style={{ background: "#22c55e20", color: "#22c55e", border: "1px solid #22c55e40" }}>
                  {sesionActiva.rol || "Sin rol"}
                </div>
                <div className="session-meta">
                  <span>ID: {(sesionActiva.id_whatsapp_session || "").slice(0, 8)}...</span>
                </div>
              </div>
              <div className="session-card-actions">
                <button className="btn btn-sm btn-danger" onClick={() => handleLogout(sesionActiva)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Cerrar Sesión
                </button>
              </div>
            </div>
          )}

          {/* QR DISPLAY INLINE (after connect or pending/qr/logged_out session) */}
          {qrDataUrl && !conectado && (
            <div className="card qr-display-card">
              <h3>📱 Escanea el código QR</h3>
              <p className="form-hint">
                Abre WhatsApp en tu teléfono → Menú → WhatsApp Web → Escanea
              </p>
              <div className="qr-image-container">
                <div className="qr-image">
                  <img src={qrDataUrl} alt="QR WhatsApp" />
                </div>
              </div>
              <div className="qr-status">
                <span className={`qr-status-dot ${pollingStatus === "connected" ? "connected" : ""}`} />
                <span>
                  {pollingStatus === "connected"
                    ? "✅ Conectado"
                    : pollingStatus === "qr" || pollingStatus === "pending"
                      ? "⏳ Esperando escaneo..."
                      : `Estado: ${(pollingStatus && STATUS_LABELS[pollingStatus]) || pollingStatus || "..."}`}
                </span>
              </div>
              <div className="qr-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => {
                  setQrDataUrl(null);
                  if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                }}>
                  Ocultar
                </button>
              </div>
            </div>
          )}

          {/* CREATE FORM - only when no sessions */}
          {noHaySesiones && showCreate && (
            <div className="card create-session-card">
              <h3>Conectar número WhatsApp</h3>
              <p className="form-hint">Ingresa el número de teléfono de la empresa.</p>
              <form onSubmit={handleCreate} className="create-session-form">
                <div className="form-row three-col">
                  <div className="form-group">
                    <label>Número *</label>
                    <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: 593984540454" required />
                  </div>
                  <div className="form-group">
                    <label>Rol</label>
                    <select value={rol} onChange={e => setRol(e.target.value)} required>
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Etiqueta</label>
                    <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="Línea principal" />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={creating}>
                    {creating ? "Conectando..." : "Conectar"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* EMPTY STATE - no sessions */}
          {noHaySesiones && !showCreate && !qrDataUrl && (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 7V4h16v3" /><path d="M9 20h6" /><path d="M12 4v8" /><path d="M4 11v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
              </svg>
              <h3>No hay sesión configurada</h3>
              <p>Conecta un número de WhatsApp para empezar a gestionar chats.</p>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Conectar Número</button>
            </div>
          )}

          {/* NOT CONNECTED - session exists -> show action buttons */}
          {haySesionDesconectada && !qrDataUrl && (
            <div className="sessions-list">
              {sesiones.map((session) => {
                const sid = session.id_whatsapp_session || "";
                const rolInfo = getRolInfo(session.rol);
                return (
                  <div key={sid} className="card session-card">
                    <div className="session-card-header">
                      <div className="session-phone">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        <span className="session-phone-number">{session.phone || "—"}</span>
                      </div>
                      <div className="session-status-row">
                        <div className="session-status-dot" style={{ background: STATUS_COLORS[session.status] }} />
                        <span className="session-status-txt" style={{ color: STATUS_COLORS[session.status] }}>
                          {session.rol_display || STATUS_LABELS[session.status] || session.status}
                        </span>
                      </div>
                    </div>
                    <div className="session-card-body">
                      <div className="rol-badge" style={{ background: `${rolInfo.color}20`, color: rolInfo.color, border: `1px solid ${rolInfo.color}40` }}>
                        {rolInfo.label}
                      </div>
                      {session.label && <div className="session-label">{session.label}</div>}
                      <div className="session-meta">
                        <span>ID: {sid.slice(0, 8)}...</span>
                      </div>
                    </div>
                    <div className="session-card-actions">
                      {/* pending / qr / logged_out -> show QR button */}
                      {(session.status === "qr" || session.status === "pending" || session.status === "logged_out") && (
                        <button className="btn btn-sm btn-primary" onClick={() => handleShowQR(session)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                          </svg>
                          Ver QR
                        </button>
                      )}
                      {/* disconnected -> show "Reconectar" */}
                      {session.status === "disconnected" && (
                        <button className="btn btn-sm btn-primary" onClick={() => handleShowQR(session)}>
                          Reconectar
                        </button>
                      )}
                      <button className="btn btn-sm btn-danger-outline btn-sm-spaced" onClick={() => handleDelete(session)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        Eliminar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* QR Modal */}
      {qrModal && (
        <QRCodeModal
          phone={qrModal.phone}
          rolLabel={qrModal.rolLabel}
          qrDataUrl={qrModal.qrDataUrl}
          status={qrModal.status}
          onClose={() => setQrModal(null)}
        />
      )}
    </div>
  );
}